import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Calendar,
  Check,
  Copy,
  FileText,
  History,
  Mail,
  Megaphone,
  Phone,
  Plus,
  RefreshCw,
  Save,
  Star,
  TrendingUp,
  AlertTriangle,
  Users,
  Video,
  Mic,
  UtensilsCrossed,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useLeagueStore } from '../../lib/leagueStore';
import { usePageTracking } from '../../lib/hooks/usePageTracking';
import {
  getOutreachContacts,
  getOutreachInteractions,
  getOutreachTemplates,
  OutreachChannel,
  OutreachContactRecord,
  OutreachContactStatus,
  OutreachInteractionOutcome,
  OutreachInteractionRecord,
  OutreachInteractionType,
  OutreachPriority,
  OutreachTemplateRecord,
  saveOutreachContact,
  saveOutreachInteraction,
  saveOutreachTemplate,
  supabase,
} from '../../lib/supabase';
import { useUserStore } from '../../lib/userStore';

interface LeaguePlayer {
  playerid: number;
  skaterfullname: string;
  positioncode: string;
  teamabbrevs: string;
  goals: number;
  assists: number;
  points: number;
  gamesplayed: number;
  salary: number;
  user_id: number;
  league_id: number;
  isnhl: boolean;
  is_scratch: boolean;
}

interface PlayerWithMeta extends LeaguePlayer {
  pointsPerGame: number;
  expectedPointsPerGame: number;
  performanceRatio: number;
}

interface GlobalTopScorer {
  player_id: number;
  skaterfullname: string;
  teamabbrevs: string;
  positioncode: string;
  points_last_7_days: number;
  goals_last_7_days: number;
  assists_last_7_days: number;
  games_played_last_7_days: number;
}

interface TemplateFormState {
  id?: number;
  channel: OutreachChannel;
  title: string;
  subject: string;
  message: string;
  is_active: boolean;
}

interface ContactFormState {
  id?: number;
  channel: OutreachChannel;
  name: string;
  platform_name: string;
  role_title: string;
  email: string;
  phone: string;
  city: string;
  status: OutreachContactStatus;
  priority: OutreachPriority;
  next_follow_up_at: string;
  notes: string;
}

interface InteractionFormState {
  id?: number;
  template_id: string;
  interaction_type: OutreachInteractionType;
  subject: string;
  content: string;
  outcome: OutreachInteractionOutcome;
  interaction_date: string;
  follow_up_date: string;
}

type PostType = 'top5' | 'sleeper' | 'bust';
type MarketingSection = 'publications' | 'templates' | 'crm';

const POSITION_LABELS: Record<string, string> = {
  C: 'Centre',
  L: 'Ailier gauche',
  R: 'Ailier droit',
  D: 'Défenseur',
  G: 'Gardien',
};

const BASE_PPG_BY_POSITION: Record<string, number> = {
  C: 0.7,
  L: 0.6,
  R: 0.6,
  D: 0.4,
  G: 0.1,
};

const CHANNEL_OPTIONS: Array<{ value: OutreachChannel; label: string; icon: React.ElementType }> = [
  { value: 'podcast', label: 'Podcast', icon: Mic },
  { value: 'youtube', label: 'YouTube', icon: Video },
  { value: 'restaurant', label: 'Restaurant sportif', icon: UtensilsCrossed },
];

const STATUS_OPTIONS: Array<{ value: OutreachContactStatus; label: string }> = [
  { value: 'a_contacter', label: 'À contacter' },
  { value: 'contacte', label: 'Contacté' },
  { value: 'relance_1', label: 'Relance 1' },
  { value: 'relance_2', label: 'Relance 2' },
  { value: 'interesse', label: 'Intéressé' },
  { value: 'partenariat', label: 'Partenariat' },
  { value: 'sans_reponse', label: 'Sans réponse' },
];

const PRIORITY_OPTIONS: Array<{ value: OutreachPriority; label: string }> = [
  { value: 'basse', label: 'Basse' },
  { value: 'moyenne', label: 'Moyenne' },
  { value: 'haute', label: 'Haute' },
];

const INTERACTION_TYPE_OPTIONS: Array<{ value: OutreachInteractionType; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'dm', label: 'DM / message' },
  { value: 'appel', label: 'Appel' },
  { value: 'rencontre', label: 'Rencontre' },
  { value: 'note', label: 'Note interne' },
];

const INTERACTION_OUTCOME_OPTIONS: Array<{ value: OutreachInteractionOutcome; label: string }> = [
  { value: 'en_attente', label: 'En attente' },
  { value: 'positif', label: 'Positif' },
  { value: 'neutre', label: 'Neutre' },
  { value: 'negatif', label: 'Négatif' },
  { value: 'sans_reponse', label: 'Sans réponse' },
];

const DEFAULT_TEMPLATE_VARIABLES = {
  contact_name: 'Jean',
  platform_name: 'Le Podcast Hockey',
  recent_topic: 'vos sleepers pour la saison',
  your_name: 'Sauro',
  league_name: 'DynastyPool',
  cta: 'ouvrir à un partenariat test de 30 jours mesurable',
};

const EMPTY_TEMPLATE_FORM: TemplateFormState = {
  channel: 'podcast',
  title: '',
  subject: '',
  message: '',
  is_active: true,
};

const EMPTY_CONTACT_FORM: ContactFormState = {
  channel: 'podcast',
  name: '',
  platform_name: '',
  role_title: '',
  email: '',
  phone: '',
  city: '',
  status: 'a_contacter',
  priority: 'moyenne',
  next_follow_up_at: '',
  notes: '',
};

const EMPTY_INTERACTION_FORM: InteractionFormState = {
  template_id: '',
  interaction_type: 'email',
  subject: '',
  content: '',
  outcome: 'en_attente',
  interaction_date: new Date().toISOString().slice(0, 10),
  follow_up_date: '',
};

function computeExpectedPPG(salary: number, position: string): number {
  const base = BASE_PPG_BY_POSITION[position] ?? 0.5;
  const salaryFactor = Math.min(1 + salary / 10_000_000, 2);
  return base * salaryFactor;
}

function renderTemplate(text: string, variables: Record<string, string>) {
  return text.replace(/{{\s*([a-z_]+)\s*}}/gi, (_, key: string) => variables[key] ?? `{{${key}}}`);
}

function getChannelLabel(channel: OutreachChannel) {
  return CHANNEL_OPTIONS.find((option) => option.value === channel)?.label ?? channel;
}

function getStatusLabel(status: OutreachContactStatus) {
  return STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

function getStatusBadgeClass(status: OutreachContactStatus) {
  switch (status) {
    case 'partenariat':
      return 'bg-success-subtle text-success';
    case 'interesse':
      return 'bg-info-subtle text-info';
    case 'sans_reponse':
      return 'bg-secondary-subtle text-secondary';
    case 'relance_2':
      return 'bg-warning-subtle text-warning';
    case 'relance_1':
      return 'bg-primary-subtle text-primary';
    case 'contacte':
      return 'bg-light text-dark';
    default:
      return 'bg-danger-subtle text-danger';
  }
}

function getPriorityBadgeClass(priority: OutreachPriority) {
  switch (priority) {
    case 'haute':
      return 'bg-danger-subtle text-danger';
    case 'moyenne':
      return 'bg-warning-subtle text-warning';
    default:
      return 'bg-secondary-subtle text-secondary';
  }
}

function formatDate(dateValue?: string | null) {
  if (!dateValue) return 'Non planifié';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return 'Date invalide';
  return date.toLocaleDateString('fr-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function toIsoDate(dateValue: string) {
  if (!dateValue) return undefined;
  return `${dateValue}T12:00:00.000Z`;
}

function buildSuggestedTemplates(leagueName: string): TemplateFormState[] {
  return [
    {
      channel: 'podcast',
      title: 'Premier contact podcast',
      subject: `Collaboration hockey fantasy possible avec ${leagueName}`,
      message: [
        'Bonjour {{contact_name}},',
        '',
        'Je t’écris parce que j’aime beaucoup {{platform_name}}, surtout votre contenu sur {{recent_topic}}.',
        `${leagueName} aide les ligues dynastie à mieux gérer leur fantasy hockey avec des outils concrets et du contenu simple à partager.`,
        '',
        'Je pense qu’il y a un bon fit pour une collaboration légère et mesurable.',
        'Si ça t’intéresse, je peux te proposer un test simple avec un lien dédié pour ton audience.',
        '',
        'Ouvert à en jaser?',
        '',
        '{{your_name}}',
      ].join('\n'),
      is_active: true,
    },
    {
      channel: 'youtube',
      title: 'Pitch créateur YouTube',
      subject: `Idée de collab YouTube hockey fantasy pour ${leagueName}`,
      message: [
        'Salut {{contact_name}},',
        '',
        'Je te contacte après avoir vu ton contenu sur {{recent_topic}} sur {{platform_name}}.',
        `${leagueName} cherche des collaborations francos alignées hockey/fantasy au Québec.`,
        '',
        'Idée simple: un segment commandité ou un défi roster/dynastie avec un lien tracké pour mesurer les inscriptions.',
        'Mon objectif est clair: {{cta}}.',
        '',
        'Si tu veux, je peux t’envoyer une proposition ultra courte en 5 lignes.',
        '',
        '{{your_name}}',
      ].join('\n'),
      is_active: true,
    },
    {
      channel: 'restaurant',
      title: 'Approche restaurant sportif',
      subject: `Soirée hockey fantasy locale avec ${leagueName}`,
      message: [
        'Bonjour {{contact_name}},',
        '',
        'Je vous contacte au sujet de {{platform_name}}.',
        `${leagueName} développe une communauté hockey fantasy francophone et on cherche des lieux partenaires pour organiser une activation locale simple.`,
        '',
        'L’idée serait une soirée hockey fantasy avec visibilité croisée, code promo dédié et suivi des inscriptions générées.',
        'Si c’est pertinent pour vous, je peux vous envoyer un concept en quelques lignes.',
        '',
        '{{your_name}}',
      ].join('\n'),
      is_active: true,
    },
  ];
}

export function MarketingTools() {
  usePageTracking('marketing_tools');
  const navigate = useNavigate();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const currentUserId = useUserStore((state) => state.user_id);

  const [players, setPlayers] = useState<PlayerWithMeta[]>([]);
  const [globalTopScorers, setGlobalTopScorers] = useState<GlobalTopScorer[]>([]);
  const [globalTopScorersUnavailable, setGlobalTopScorersUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activePost, setActivePost] = useState<PostType>('top5');
  const [copiedPost, setCopiedPost] = useState<PostType | null>(null);

  const [activeSection, setActiveSection] = useState<MarketingSection>('publications');
  const [templates, setTemplates] = useState<OutreachTemplateRecord[]>([]);
  const [contacts, setContacts] = useState<OutreachContactRecord[]>([]);
  const [interactions, setInteractions] = useState<OutreachInteractionRecord[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  const [templateForm, setTemplateForm] = useState<TemplateFormState>(EMPTY_TEMPLATE_FORM);
  const [contactForm, setContactForm] = useState<ContactFormState>(EMPTY_CONTACT_FORM);
  const [interactionForm, setInteractionForm] = useState<InteractionFormState>(EMPTY_INTERACTION_FORM);
  const [templateVariables, setTemplateVariables] = useState<Record<string, string>>(DEFAULT_TEMPLATE_VARIABLES);
  const [statusFilter, setStatusFilter] = useState<'tous' | OutreachContactStatus>('tous');
  const [channelFilter, setChannelFilter] = useState<'tous' | OutreachChannel>('tous');
  const [outreachLoading, setOutreachLoading] = useState(false);
  const [interactionsLoading, setInteractionsLoading] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [savingInteraction, setSavingInteraction] = useState(false);

  const isAuthorized = Number(currentUserId) === 3;
  const leagueId = selectedLeague?.id ?? null;
  const leagueName = selectedLeague?.name ?? 'notre ligue';
  const today = new Date().toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' });

  const fetchMarketingData = useCallback(async () => {
    if (!leagueId) return;
    setLoading(true);
    try {
      const [leaguePlayersResult, globalTopScorersResult] = await Promise.all([
        supabase
          .from('getmyteamplayer')
          .select('playerid, skaterfullname, positioncode, teamabbrevs, goals, assists, points, gamesplayed, salary, user_id, league_id, isnhl, is_scratch')
          .eq('league_id', leagueId)
          .eq('isnhl', true)
          .eq('is_scratch', false),
        supabase.rpc('get_global_top_scorers_last_7_days', { p_limit: 5 }),
      ]);

      const { data: leaguePlayersData, error: leaguePlayersError } = leaguePlayersResult;

      if (leaguePlayersError && (leaguePlayersError as { code?: string }).code !== 'PGRST116') {
        throw leaguePlayersError;
      }

      const enriched: PlayerWithMeta[] = (leaguePlayersData ?? [])
        .filter((player: LeaguePlayer) => player.gamesplayed > 0)
        .map((player: LeaguePlayer) => {
          const pointsPerGame = player.points / player.gamesplayed;
          const expectedPointsPerGame = computeExpectedPPG(player.salary, player.positioncode);
          return {
            ...player,
            pointsPerGame,
            expectedPointsPerGame,
            performanceRatio: expectedPointsPerGame > 0 ? pointsPerGame / expectedPointsPerGame : 0,
          };
        });

      setPlayers(enriched);

      const { data: globalTopScorersData, error: globalTopScorersError } = globalTopScorersResult;

      if (globalTopScorersError) {
        const errorCode = (globalTopScorersError as { code?: string }).code;

        if (errorCode === '57014') {
          console.warn('MarketingTools global top scorers timeout:', globalTopScorersError);
          setGlobalTopScorers([]);
          setGlobalTopScorersUnavailable(true);
          toast.error('Le Top 5 global des 7 derniers jours est temporairement indisponible.');
        } else if (errorCode !== 'PGRST116') {
          throw globalTopScorersError;
        }
      } else {
        setGlobalTopScorers((globalTopScorersData ?? []) as GlobalTopScorer[]);
        setGlobalTopScorersUnavailable(false);
      }
    } catch (error) {
      console.error('MarketingTools fetch error:', error);
      toast.error('Erreur lors du chargement des données marketing');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  const fetchOutreachData = useCallback(async () => {
    if (!leagueId || !currentUserId || !isAuthorized) return;
    setOutreachLoading(true);
    try {
      const [templatesResult, contactsResult] = await Promise.all([
        getOutreachTemplates(leagueId, currentUserId),
        getOutreachContacts(leagueId, currentUserId),
      ]);

      if (templatesResult.error) {
        throw templatesResult.error;
      }

      if (contactsResult.error) {
        throw contactsResult.error;
      }

      setTemplates(templatesResult.data);
      setContacts(contactsResult.data);
      setSelectedContactId((currentSelected) => {
        if (currentSelected && contactsResult.data.some((contact) => contact.id === currentSelected)) {
          return currentSelected;
        }
        return contactsResult.data[0]?.id ?? null;
      });
    } catch (error) {
      console.error('Outreach fetch error:', error);
      toast.error('Erreur lors du chargement du CRM outreach');
    } finally {
      setOutreachLoading(false);
    }
  }, [currentUserId, isAuthorized, leagueId]);

  const fetchInteractions = useCallback(async (contactId: number) => {
    if (!leagueId || !currentUserId || !isAuthorized) return;
    setInteractionsLoading(true);
    try {
      const result = await getOutreachInteractions(contactId, leagueId, currentUserId);
      if (result.error) {
        throw result.error;
      }
      setInteractions(result.data);
    } catch (error) {
      console.error('Interactions fetch error:', error);
      toast.error('Erreur lors du chargement des suivis');
    } finally {
      setInteractionsLoading(false);
    }
  }, [currentUserId, isAuthorized, leagueId]);

  useEffect(() => {
    if (!leagueId || !isAuthorized) return;
    fetchMarketingData();
    fetchOutreachData();
  }, [fetchMarketingData, fetchOutreachData, isAuthorized, leagueId]);

  useEffect(() => {
    if (!selectedContactId) {
      setInteractions([]);
      return;
    }
    fetchInteractions(selectedContactId);
  }, [fetchInteractions, selectedContactId]);

  const top5 = [...globalTopScorers]
    .sort((a, b) => b.points_last_7_days - a.points_last_7_days)
    .slice(0, 5);

  const sleepers = [...players]
    .filter((player) => player.gamesplayed >= 5 && player.performanceRatio > 1)
    .sort((a, b) => b.performanceRatio - a.performanceRatio)
    .slice(0, 3);

  const busts = [...players]
    .filter((player) => player.gamesplayed >= 5 && player.salary >= 2_000_000 && player.performanceRatio < 1)
    .sort((a, b) => a.performanceRatio - b.performanceRatio)
    .slice(0, 3);

  function buildTop5Post() {
    if (globalTopScorersUnavailable) {
      return '(Top 5 global des 7 derniers jours temporairement indisponible. Réessayez plus tard.)';
    }

    if (top5.length === 0) return '(Aucune donnée disponible)';
    const lines = top5.map(
      (player, index) =>
        `${['🥇', '🥈', '🥉', '4️⃣', '5️⃣'][index]} ${player.skaterfullname} (${player.teamabbrevs}, ${POSITION_LABELS[player.positioncode] ?? player.positioncode}) — ${player.points_last_7_days} pts en ${player.games_played_last_7_days} parties · ${player.goals_last_7_days} B, ${player.assists_last_7_days} A`
    );

    return [
      '📈 Performances joueurs NHL',
      `📅 ${today}`,
      '',
      '🏆 TOP 5 DES MEILLEURS POINTEURS - 7 DERNIERS JOURS',
      '',
      ...lines,
      '',
      '👉 Classement global basé sur les points réels accumulés partout dans les 7 derniers jours.',
      '',
      '#DynastyPool #HockeyFantasy #LigueHockey',
    ].join('\n');
  }

  function buildSleeperPost() {
    if (sleepers.length === 0) return '(Aucun sleeper détecté cette semaine)';
    const lines = sleepers.map(
      (player) =>
        `🔥 ${player.skaterfullname} (${player.teamabbrevs}, ${POSITION_LABELS[player.positioncode] ?? player.positioncode}) — ${player.points} pts en ${player.gamesplayed} parties · Ratio perf : ${(player.performanceRatio * 100).toFixed(0)}% vs projection`
    );

    return [
      `📈 Performances joueurs — ${leagueName}`,
      `📅 ${today}`,
      '',
      '💎 SLEEPER QUI EXPLOSE',
      '',
      ...lines,
      '',
      '👉 Ces joueurs surpassent largement les attentes — si vous les avez, ne les lâchez surtout pas.',
      '',
      '#DynastyPool #HockeyFantasy #SleeperAlert',
    ].join('\n');
  }

  function buildBustPost() {
    if (busts.length === 0) return '(Aucun bust détecté cette semaine)';
    const lines = busts.map(
      (player) =>
        `📉 ${player.skaterfullname} (${player.teamabbrevs}) — ${player.points} pts en ${player.gamesplayed} parties · Salaire : ${(player.salary / 1_000_000).toFixed(1)}M$ · Rendement : ${(player.performanceRatio * 100).toFixed(0)}% vs projection`
    );

    return [
      `📈 Performances joueurs — ${leagueName}`,
      `📅 ${today}`,
      '',
      '🚨 BUST ALERT',
      '',
      ...lines,
      '',
      '👉 Ces joueurs sous-performent par rapport à leur salaire — à surveiller de près.',
      '',
      '#DynastyPool #HockeyFantasy #BustAlert',
    ].join('\n');
  }

  const postConfig: Record<PostType, { label: string; icon: React.ElementType; color: string; build: () => string }> = {
    top5: { label: 'Top 5 global - 7 jours', icon: Star, color: '#facc15', build: buildTop5Post },
    sleeper: { label: 'Sleeper qui explose', icon: TrendingUp, color: '#22c55e', build: buildSleeperPost },
    bust: { label: 'Bust Alert', icon: AlertTriangle, color: '#ef4444', build: buildBustPost },
  };

  const activeConfig = postConfig[activePost];
  const activeText = activeConfig.build();
  const activeNote =
    activePost === 'top5'
      ? globalTopScorersUnavailable
        ? 'Le classement global NHL des 7 derniers jours est temporairement indisponible car la requête a dépassé le temps maximal autorisé.'
        : 'Basé sur le classement global NHL des 7 derniers jours calculé à partir des snapshots historiques de la table players.'
      : 'Basé sur les points cumulés cette saison et le ratio pts/match vs projection selon le poste et le salaire du joueur. Les réservistes et scratches sont exclus.';

  const filteredContacts = useMemo(() => {
    return [...contacts]
      .filter((contact) => (statusFilter === 'tous' ? true : contact.status === statusFilter))
      .filter((contact) => (channelFilter === 'tous' ? true : contact.channel === channelFilter))
      .sort((left, right) => {
        const leftDate = left.next_follow_up_at ? new Date(left.next_follow_up_at).getTime() : Number.MAX_SAFE_INTEGER;
        const rightDate = right.next_follow_up_at ? new Date(right.next_follow_up_at).getTime() : Number.MAX_SAFE_INTEGER;
        return leftDate - rightDate;
      });
  }, [channelFilter, contacts, statusFilter]);

  const selectedContact = contacts.find((contact) => contact.id === selectedContactId) ?? null;
  const templatePreviewSubject = renderTemplate(templateForm.subject || '(Sans objet)', templateVariables);
  const templatePreviewMessage = renderTemplate(templateForm.message || '(Message vide)', templateVariables);

  async function handleCopyText(text: string, copyKey?: PostType | null) {
    try {
      await navigator.clipboard.writeText(text);
      if (copyKey) {
        setCopiedPost(copyKey);
        setTimeout(() => setCopiedPost(null), 3000);
      }
      toast.success('Texte copié dans le presse-papiers');
    } catch {
      toast.error('Impossible de copier automatiquement. Sélectionnez le texte manuellement.');
    }
  }

  async function handleRefreshAll() {
    await Promise.all([fetchMarketingData(), fetchOutreachData()]);
    if (selectedContactId) {
      await fetchInteractions(selectedContactId);
    }
  }

  async function handleSaveTemplate() {
    if (!leagueId || !currentUserId) return;
    if (!templateForm.title.trim() || !templateForm.message.trim()) {
      toast.error('Le titre et le message sont obligatoires');
      return;
    }

    setSavingTemplate(true);
    const result = await saveOutreachTemplate({
      id: templateForm.id,
      league_id: leagueId,
      user_id: currentUserId,
      channel: templateForm.channel,
      title: templateForm.title.trim(),
      subject: templateForm.subject.trim() || null,
      message: templateForm.message,
      is_active: templateForm.is_active,
    });
    setSavingTemplate(false);

    if (!result.success || !result.data) {
      toast.error(result.error?.message || 'Erreur lors de la sauvegarde du modèle');
      return;
    }

    toast.success(templateForm.id ? 'Modèle mis à jour' : 'Modèle créé');
    setTemplateForm(EMPTY_TEMPLATE_FORM);
    await fetchOutreachData();
  }

  async function handleCreateSuggestedTemplates() {
    if (!leagueId || !currentUserId) return;
    setSavingTemplate(true);

    try {
      const suggestedTemplates = buildSuggestedTemplates(leagueName);
      const results = await Promise.all(
        suggestedTemplates.map((template) =>
          saveOutreachTemplate({
            league_id: leagueId,
            user_id: currentUserId,
            channel: template.channel,
            title: template.title,
            subject: template.subject,
            message: template.message,
            is_active: template.is_active,
          })
        )
      );

      const hasError = results.some((result) => !result.success);
      if (hasError) {
        throw new Error('Impossible de créer tous les modèles suggérés');
      }

      toast.success('Modèles suggérés créés');
      await fetchOutreachData();
    } catch (error) {
      console.error('Suggested templates error:', error);
      toast.error('Erreur lors de la création des modèles suggérés');
    } finally {
      setSavingTemplate(false);
    }
  }

  async function handleSaveContact() {
    if (!leagueId || !currentUserId) return;
    if (!contactForm.name.trim()) {
      toast.error('Le nom du contact est obligatoire');
      return;
    }

    setSavingContact(true);
    const result = await saveOutreachContact({
      id: contactForm.id,
      league_id: leagueId,
      user_id: currentUserId,
      channel: contactForm.channel,
      name: contactForm.name.trim(),
      platform_name: contactForm.platform_name.trim() || null,
      role_title: contactForm.role_title.trim() || null,
      email: contactForm.email.trim() || null,
      phone: contactForm.phone.trim() || null,
      city: contactForm.city.trim() || null,
      status: contactForm.status,
      priority: contactForm.priority,
      next_follow_up_at: contactForm.next_follow_up_at || null,
      notes: contactForm.notes.trim() || null,
    });
    setSavingContact(false);

    if (!result.success || !result.data) {
      toast.error(result.error?.message || 'Erreur lors de la sauvegarde du contact');
      return;
    }

    toast.success(contactForm.id ? 'Contact mis à jour' : 'Contact ajouté');
    setContactForm(EMPTY_CONTACT_FORM);
    await fetchOutreachData();
    setSelectedContactId(result.data.id);
  }

  async function handleSaveInteraction() {
    if (!leagueId || !currentUserId || !selectedContactId) return;
    if (!interactionForm.content.trim()) {
      toast.error('Le contenu du suivi est obligatoire');
      return;
    }

    setSavingInteraction(true);
    const result = await saveOutreachInteraction({
      id: interactionForm.id,
      contact_id: selectedContactId,
      league_id: leagueId,
      user_id: currentUserId,
      template_id: interactionForm.template_id ? Number(interactionForm.template_id) : null,
      interaction_type: interactionForm.interaction_type,
      subject: interactionForm.subject.trim() || null,
      content: interactionForm.content,
      outcome: interactionForm.outcome,
      interaction_date: toIsoDate(interactionForm.interaction_date),
      follow_up_date: interactionForm.follow_up_date || null,
    });
    setSavingInteraction(false);

    if (!result.success) {
      toast.error(result.error?.message || 'Erreur lors de la sauvegarde du suivi');
      return;
    }

    toast.success('Suivi enregistré');
    setInteractionForm(EMPTY_INTERACTION_FORM);
    await Promise.all([fetchOutreachData(), fetchInteractions(selectedContactId)]);
  }

  function loadTemplate(template: OutreachTemplateRecord) {
    setTemplateForm({
      id: template.id,
      channel: template.channel,
      title: template.title,
      subject: template.subject ?? '',
      message: template.message,
      is_active: template.is_active,
    });
    setActiveSection('templates');
  }

  function loadContact(contact: OutreachContactRecord) {
    setSelectedContactId(contact.id);
    setContactForm({
      id: contact.id,
      channel: contact.channel,
      name: contact.name,
      platform_name: contact.platform_name ?? '',
      role_title: contact.role_title ?? '',
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      city: contact.city ?? '',
      status: contact.status,
      priority: contact.priority,
      next_follow_up_at: contact.next_follow_up_at ?? '',
      notes: contact.notes ?? '',
    });
    setActiveSection('crm');
  }

  function handleTemplatePickForInteraction(templateId: string) {
    setInteractionForm((current) => {
      const nextState = { ...current, template_id: templateId };
      if (!templateId) {
        return nextState;
      }
      const template = templates.find((item) => item.id === Number(templateId));
      if (!template) {
        return nextState;
      }
      return {
        ...nextState,
        subject: renderTemplate(template.subject ?? '', templateVariables),
        content: renderTemplate(template.message, {
          ...templateVariables,
          contact_name: selectedContact?.name ?? templateVariables.contact_name,
          platform_name: selectedContact?.platform_name ?? templateVariables.platform_name,
        }),
      };
    });
  }

  if (!selectedLeague) {
    navigate('/dashboard');
    return null;
  }

  if (!isAuthorized) {
    navigate('/my-office');
    return null;
  }

  return (
    <div className="card shadow-lg border-0">
      <div className="card-body p-4 p-md-5">
        <div className="d-flex align-items-center mb-4">
          <button
            className="btn btn-link p-0 me-3"
            onClick={() => navigate('/my-office')}
            title="Retour au bureau"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="d-flex align-items-center gap-2">
            <Megaphone className="text-primary" size={28} />
            <div>
              <h4 className="mb-0 fw-bold">Outils marketing</h4>
              <p className="text-muted small mb-0">{selectedLeague.name}</p>
            </div>
          </div>
          <button
            className="btn btn-sm btn-outline-secondary ms-auto d-flex align-items-center gap-1"
            onClick={handleRefreshAll}
            disabled={loading || outreachLoading || interactionsLoading}
            title="Rafraîchir les données"
          >
            <RefreshCw size={14} className={loading || outreachLoading || interactionsLoading ? 'spinning' : ''} />
            Actualiser
          </button>
        </div>

        <p className="text-muted mb-4">
          Générez des publications prêtes à partager, préparez vos messages d’outreach et suivez vos contacts dans un CRM simple réservé à votre compte admin.
        </p>

        <div className="d-flex flex-wrap gap-2 mb-4">
          <button
            className={`btn btn-sm d-flex align-items-center gap-2 fw-semibold ${activeSection === 'publications' ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setActiveSection('publications')}
          >
            <Megaphone size={15} />
            Publications
          </button>
          <button
            className={`btn btn-sm d-flex align-items-center gap-2 fw-semibold ${activeSection === 'templates' ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setActiveSection('templates')}
          >
            <FileText size={15} />
            Messages outreach
          </button>
          <button
            className={`btn btn-sm d-flex align-items-center gap-2 fw-semibold ${activeSection === 'crm' ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setActiveSection('crm')}
          >
            <Users size={15} />
            CRM interne
          </button>
        </div>

        {activeSection === 'publications' && (
          <>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="text-muted mt-3 small">Chargement des données joueurs...</p>
              </div>
            ) : (
              <>
                <div className="d-flex flex-wrap gap-2 mb-4">
                  {(Object.entries(postConfig) as [PostType, typeof postConfig[PostType]][]).map(([type, config]) => {
                    const Icon = config.icon;
                    const isActive = activePost === type;
                    return (
                      <button
                        key={type}
                        className={`btn btn-sm d-flex align-items-center gap-2 fw-semibold ${isActive ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setActivePost(type)}
                      >
                        <Icon size={15} style={isActive ? {} : { color: config.color }} />
                        {config.label}
                      </button>
                    );
                  })}
                </div>

                <div
                  className="card border-0 mb-3"
                  style={{
                    background: 'linear-gradient(135deg, rgba(139,92,246,0.06) 0%, rgba(59,130,246,0.06) 100%)',
                    borderLeft: `4px solid ${activeConfig.color}`,
                  }}
                >
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2 gap-3 flex-wrap">
                      <span
                        className="badge rounded-pill"
                        style={{
                          backgroundColor: activeConfig.color,
                          color: activePost === 'top5' ? '#1f2937' : 'white',
                          fontSize: '0.75rem',
                        }}
                      >
                        {activeConfig.label}
                      </span>
                      <button
                        className="btn btn-sm btn-primary d-flex align-items-center gap-2 fw-semibold"
                        onClick={() => handleCopyText(activeText, activePost)}
                      >
                        {copiedPost === activePost ? (
                          <>
                            <Check size={15} />
                            Copié
                          </>
                        ) : (
                          <>
                            <Copy size={15} />
                            Copier le texte
                          </>
                        )}
                      </button>
                    </div>
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'inherit',
                        fontSize: '0.875rem',
                        lineHeight: '1.6',
                        margin: 0,
                        userSelect: 'all',
                      }}
                    >
                      {activeText}
                    </pre>
                  </div>
                </div>

                <p className="text-muted small mb-0">
                  <strong>Basé sur :</strong> {activeNote}
                </p>
              </>
            )}
          </>
        )}

        {activeSection === 'templates' && (
          <>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
              <div>
                <h5 className="fw-bold mb-1">Messages prêts à copier</h5>
                <p className="text-muted small mb-0">
                  Créez vos pitchs outreach pour podcasts, chaînes YouTube et restos sportifs avec variables dynamiques.
                </p>
              </div>
              <button
                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
                onClick={handleCreateSuggestedTemplates}
                disabled={savingTemplate}
              >
                <Plus size={15} />
                Créer 3 modèles suggérés
              </button>
            </div>

            <div className="row g-4 mb-4">
              <div className="col-lg-6">
                <div className="card border-0 bg-body-tertiary h-100">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="fw-bold mb-0">Éditeur de modèle</h6>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setTemplateForm(EMPTY_TEMPLATE_FORM)}
                      >
                        Nouveau
                      </button>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Canal</label>
                      <select
                        className="form-select"
                        value={templateForm.channel}
                        onChange={(event) => setTemplateForm((current) => ({ ...current, channel: event.target.value as OutreachChannel }))}
                      >
                        {CHANNEL_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Titre interne</label>
                      <input
                        type="text"
                        className="form-control"
                        value={templateForm.title}
                        onChange={(event) => setTemplateForm((current) => ({ ...current, title: event.target.value }))}
                        placeholder="Ex: Premier contact podcast"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Sujet</label>
                      <input
                        type="text"
                        className="form-control"
                        value={templateForm.subject}
                        onChange={(event) => setTemplateForm((current) => ({ ...current, subject: event.target.value }))}
                        placeholder="Ex: Collaboration possible avec DynastyPool"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Message</label>
                      <textarea
                        className="form-control"
                        rows={11}
                        value={templateForm.message}
                        onChange={(event) => setTemplateForm((current) => ({ ...current, message: event.target.value }))}
                        placeholder={'Utilisez {{contact_name}}, {{platform_name}}, {{recent_topic}}, {{your_name}}, {{cta}}'}
                      />
                    </div>
                    <div className="form-check form-switch mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={templateForm.is_active}
                        onChange={(event) => setTemplateForm((current) => ({ ...current, is_active: event.target.checked }))}
                        id="templateActiveSwitch"
                      />
                      <label className="form-check-label" htmlFor="templateActiveSwitch">
                        Modèle actif
                      </label>
                    </div>
                    <button
                      className="btn btn-primary d-inline-flex align-items-center gap-2"
                      onClick={handleSaveTemplate}
                      disabled={savingTemplate}
                    >
                      <Save size={15} />
                      {templateForm.id ? 'Mettre à jour le modèle' : 'Enregistrer le modèle'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="card border-0 h-100" style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.08) 0%, rgba(59,130,246,0.08) 100%)' }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="fw-bold mb-0">Aperçu prêt à copier</h6>
                      <button
                        className="btn btn-sm btn-primary d-inline-flex align-items-center gap-2"
                        onClick={() => handleCopyText(`${templatePreviewSubject}\n\n${templatePreviewMessage}`)}
                      >
                        <Copy size={15} />
                        Copier l’aperçu
                      </button>
                    </div>

                    <div className="row g-2 mb-3">
                      {Object.entries(templateVariables).map(([key, value]) => (
                        <div className="col-md-6" key={key}>
                          <label className="form-label small text-muted mb-1">{`{{${key}}}`}</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={value}
                            onChange={(event) => setTemplateVariables((current) => ({ ...current, [key]: event.target.value }))}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="mb-2 small text-muted fw-semibold">Sujet rendu</div>
                    <div className="border rounded p-2 bg-white mb-3">{templatePreviewSubject}</div>
                    <div className="mb-2 small text-muted fw-semibold">Message rendu</div>
                    <pre
                      className="border rounded p-3 bg-white mb-0"
                      style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', lineHeight: '1.6', userSelect: 'all' }}
                    >
                      {templatePreviewMessage}
                    </pre>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0">Bibliothèque de modèles</h6>
                  <span className="text-muted small">{templates.length} modèle(s)</span>
                </div>
                {outreachLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                  </div>
                ) : templates.length === 0 ? (
                  <p className="text-muted mb-0">Aucun modèle sauvegardé pour le moment.</p>
                ) : (
                  <div className="row g-3">
                    {templates.map((template) => {
                      const Icon = CHANNEL_OPTIONS.find((option) => option.value === template.channel)?.icon ?? FileText;
                      return (
                        <div className="col-lg-4" key={template.id}>
                          <div className="card h-100 border-0 bg-body-tertiary">
                            <div className="card-body d-flex flex-column">
                              <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                                <span className="badge bg-primary-subtle text-primary d-inline-flex align-items-center gap-1">
                                  <Icon size={14} />
                                  {getChannelLabel(template.channel)}
                                </span>
                                <span className={`badge ${template.is_active ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
                                  {template.is_active ? 'Actif' : 'Inactif'}
                                </span>
                              </div>
                              <h6 className="fw-bold">{template.title}</h6>
                              <p className="text-muted small mb-3">{template.subject || 'Sans objet'}</p>
                              <pre
                                className="small text-muted flex-grow-1"
                                style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', maxHeight: '180px', overflow: 'hidden' }}
                              >
                                {template.message}
                              </pre>
                              <div className="d-flex gap-2 mt-3">
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => loadTemplate(template)}>
                                  Charger
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleCopyText(`${template.subject || ''}\n\n${template.message}`.trim())}
                                >
                                  Copier brut
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {activeSection === 'crm' && (
          <>
            <div className="row g-4 mb-4">
              <div className="col-xl-5">
                <div className="card border-0 bg-body-tertiary h-100">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="fw-bold mb-0">Fiche contact</h6>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setContactForm(EMPTY_CONTACT_FORM)}>
                        Nouveau
                      </button>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Canal</label>
                        <select
                          className="form-select"
                          value={contactForm.channel}
                          onChange={(event) => setContactForm((current) => ({ ...current, channel: event.target.value as OutreachChannel }))}
                        >
                          {CHANNEL_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Priorité</label>
                        <select
                          className="form-select"
                          value={contactForm.priority}
                          onChange={(event) => setContactForm((current) => ({ ...current, priority: event.target.value as OutreachPriority }))}
                        >
                          {PRIORITY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Nom du contact</label>
                        <input
                          type="text"
                          className="form-control"
                          value={contactForm.name}
                          onChange={(event) => setContactForm((current) => ({ ...current, name: event.target.value }))}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Podcast / chaîne / établissement</label>
                        <div className="input-group">
                          <span className="input-group-text">
                            <Building2 size={15} />
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            value={contactForm.platform_name}
                            onChange={(event) => setContactForm((current) => ({ ...current, platform_name: event.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Rôle / titre</label>
                        <input
                          type="text"
                          className="form-control"
                          value={contactForm.role_title}
                          onChange={(event) => setContactForm((current) => ({ ...current, role_title: event.target.value }))}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Email</label>
                        <div className="input-group">
                          <span className="input-group-text">
                            <Mail size={15} />
                          </span>
                          <input
                            type="email"
                            className="form-control"
                            value={contactForm.email}
                            onChange={(event) => setContactForm((current) => ({ ...current, email: event.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Téléphone</label>
                        <div className="input-group">
                          <span className="input-group-text">
                            <Phone size={15} />
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            value={contactForm.phone}
                            onChange={(event) => setContactForm((current) => ({ ...current, phone: event.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Ville</label>
                        <input
                          type="text"
                          className="form-control"
                          value={contactForm.city}
                          onChange={(event) => setContactForm((current) => ({ ...current, city: event.target.value }))}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Statut</label>
                        <select
                          className="form-select"
                          value={contactForm.status}
                          onChange={(event) => setContactForm((current) => ({ ...current, status: event.target.value as OutreachContactStatus }))}
                        >
                          {STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Prochaine relance</label>
                        <div className="input-group">
                          <span className="input-group-text">
                            <Calendar size={15} />
                          </span>
                          <input
                            type="date"
                            className="form-control"
                            value={contactForm.next_follow_up_at}
                            onChange={(event) => setContactForm((current) => ({ ...current, next_follow_up_at: event.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Notes</label>
                        <textarea
                          className="form-control"
                          rows={4}
                          value={contactForm.notes}
                          onChange={(event) => setContactForm((current) => ({ ...current, notes: event.target.value }))}
                        />
                      </div>
                    </div>
                    <button
                      className="btn btn-primary d-inline-flex align-items-center gap-2 mt-3"
                      onClick={handleSaveContact}
                      disabled={savingContact}
                    >
                      <Save size={15} />
                      {contactForm.id ? 'Mettre à jour le contact' : 'Créer le contact'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="col-xl-7">
                <div className="card border-0 h-100">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                      <div>
                        <h6 className="fw-bold mb-0">Pipeline contacts</h6>
                        <p className="text-muted small mb-0">{filteredContacts.length} contact(s) filtré(s)</p>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <select className="form-select form-select-sm" value={channelFilter} onChange={(event) => setChannelFilter(event.target.value as 'tous' | OutreachChannel)}>
                          <option value="tous">Tous les canaux</option>
                          {CHANNEL_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <select className="form-select form-select-sm" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as 'tous' | OutreachContactStatus)}>
                          <option value="tous">Tous les statuts</option>
                          {STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {outreachLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                      </div>
                    ) : filteredContacts.length === 0 ? (
                      <p className="text-muted mb-0">Aucun contact enregistré pour le moment.</p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table align-middle mb-0">
                          <thead>
                            <tr>
                              <th>Contact</th>
                              <th>Canal</th>
                              <th>Statut</th>
                              <th>Relance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredContacts.map((contact) => (
                              <tr
                                key={contact.id}
                                role="button"
                                className={selectedContactId === contact.id ? 'table-primary' : undefined}
                                onClick={() => loadContact(contact)}
                              >
                                <td>
                                  <div className="fw-semibold">{contact.name}</div>
                                  <div className="text-muted small">{contact.platform_name || 'Sans média associé'}</div>
                                </td>
                                <td>
                                  <span className="badge bg-light text-dark">{getChannelLabel(contact.channel)}</span>
                                </td>
                                <td>
                                  <span className={`badge ${getStatusBadgeClass(contact.status)}`}>{getStatusLabel(contact.status)}</span>
                                  <div className="mt-1">
                                    <span className={`badge ${getPriorityBadgeClass(contact.priority)}`}>{contact.priority}</span>
                                  </div>
                                </td>
                                <td>
                                  <div>{formatDate(contact.next_follow_up_at)}</div>
                                  <div className="text-muted small">Dernier contact: {formatDate(contact.last_contact_at)}</div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <div>
                    <h6 className="fw-bold mb-0">Suivi du contact</h6>
                    <p className="text-muted small mb-0">
                      {selectedContact ? `${selectedContact.name} · ${selectedContact.platform_name || 'Sans média'}` : 'Choisissez un contact pour voir son historique'}
                    </p>
                  </div>
                  {selectedContact && (
                    <span className={`badge ${getStatusBadgeClass(selectedContact.status)}`}>{getStatusLabel(selectedContact.status)}</span>
                  )}
                </div>

                {!selectedContact ? (
                  <p className="text-muted mb-0">Sélectionnez un contact dans le pipeline pour enregistrer un suivi.</p>
                ) : (
                  <div className="row g-4">
                    <div className="col-lg-5">
                      <div className="card border-0 bg-body-tertiary h-100">
                        <div className="card-body">
                          <h6 className="fw-bold mb-3">Ajouter un suivi</h6>
                          <div className="mb-3">
                            <label className="form-label">Préremplir à partir d’un modèle</label>
                            <select
                              className="form-select"
                              value={interactionForm.template_id}
                              onChange={(event) => handleTemplatePickForInteraction(event.target.value)}
                            >
                              <option value="">Aucun modèle</option>
                              {templates.map((template) => (
                                <option key={template.id} value={template.id}>
                                  {template.title}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Type</label>
                            <select
                              className="form-select"
                              value={interactionForm.interaction_type}
                              onChange={(event) => setInteractionForm((current) => ({ ...current, interaction_type: event.target.value as OutreachInteractionType }))}
                            >
                              {INTERACTION_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Résultat</label>
                            <select
                              className="form-select"
                              value={interactionForm.outcome}
                              onChange={(event) => setInteractionForm((current) => ({ ...current, outcome: event.target.value as OutreachInteractionOutcome }))}
                            >
                              {INTERACTION_OUTCOME_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Sujet</label>
                            <input
                              type="text"
                              className="form-control"
                              value={interactionForm.subject}
                              onChange={(event) => setInteractionForm((current) => ({ ...current, subject: event.target.value }))}
                            />
                          </div>
                          <div className="row g-3 mb-3">
                            <div className="col-md-6">
                              <label className="form-label">Date du suivi</label>
                              <input
                                type="date"
                                className="form-control"
                                value={interactionForm.interaction_date}
                                onChange={(event) => setInteractionForm((current) => ({ ...current, interaction_date: event.target.value }))}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label">Date de relance</label>
                              <input
                                type="date"
                                className="form-control"
                                value={interactionForm.follow_up_date}
                                onChange={(event) => setInteractionForm((current) => ({ ...current, follow_up_date: event.target.value }))}
                              />
                            </div>
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Contenu</label>
                            <textarea
                              className="form-control"
                              rows={8}
                              value={interactionForm.content}
                              onChange={(event) => setInteractionForm((current) => ({ ...current, content: event.target.value }))}
                            />
                          </div>
                          <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={handleSaveInteraction}
                            disabled={savingInteraction}
                          >
                            <Plus size={15} />
                            Enregistrer le suivi
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-7">
                      <div className="card border-0 h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="fw-bold mb-0">Historique</h6>
                            <span className="text-muted small">{interactions.length} entrée(s)</span>
                          </div>
                          {interactionsLoading ? (
                            <div className="text-center py-4">
                              <div className="spinner-border spinner-border-sm text-primary" role="status" />
                            </div>
                          ) : interactions.length === 0 ? (
                            <p className="text-muted mb-0">Aucun suivi enregistré pour ce contact.</p>
                          ) : (
                            <div className="d-flex flex-column gap-3">
                              {interactions.map((interaction) => (
                                <div key={interaction.id} className="border rounded p-3 bg-body-tertiary">
                                  <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-2">
                                    <div>
                                      <div className="fw-semibold d-flex align-items-center gap-2">
                                        <History size={15} className="text-primary" />
                                        {INTERACTION_TYPE_OPTIONS.find((option) => option.value === interaction.interaction_type)?.label || interaction.interaction_type}
                                      </div>
                                      <div className="text-muted small">{formatDate(interaction.interaction_date)}</div>
                                    </div>
                                    <span className={`badge ${interaction.outcome === 'positif' ? 'bg-success-subtle text-success' : interaction.outcome === 'negatif' ? 'bg-danger-subtle text-danger' : interaction.outcome === 'sans_reponse' ? 'bg-secondary-subtle text-secondary' : 'bg-warning-subtle text-warning'}`}>
                                      {INTERACTION_OUTCOME_OPTIONS.find((option) => option.value === interaction.outcome)?.label || interaction.outcome}
                                    </span>
                                  </div>
                                  {interaction.subject && <div className="fw-semibold mb-2">{interaction.subject}</div>}
                                  <pre className="mb-2 small" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                                    {interaction.content}
                                  </pre>
                                  <div className="text-muted small">
                                    Relance prévue: {formatDate(interaction.follow_up_date)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spinning { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
